import { useEffect } from "react";

import { Grid, Stack, Typography, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CheckBoxOutlined, CreateOutlined } from "@mui/icons-material";

import nameof from "ts-nameof.macro";
import _ from "lodash";

import { PublicRoutesConfig } from "../../RoutesConfig";
import LinearStepper from "../../../common/components/LinearStepper";
import Success from "../../shared/SuccessModal";
import DatePickerExtended from "../../../common/components/mui-extended/DatePickerExtended";
import DropzoneExtended from "../../../common/components/mui-extended/DropzoneExtended";
import SelectExtended from "../../../common/components/mui-extended/SelectExtended";
import SectionExtended from "../../../common/components/mui-extended/SectionExtended";
import TextFieldExtended from "../../../common/components/mui-extended/TextFieldExtended";
import { ErrorMessages, Rules } from "../../../common/validationRules";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import { Mode, Step, useInputForm } from "../../../common/hooks/useInputForm";
import useUserContext from "../../../hooks/useUserContext";
import authorizationService from "../../../services/authorizationService";
import registerService from "../../../services/registerService";
import fileService from "../../../common/services/fileService";
import { AuthorizationInputDto } from "../../../interfaces/Authorization";
import { dateToStringShort } from "../../../common/utils/dateTime";
import { useNavigate } from "react-router-dom";

export default function Input() {
  const navigate = useNavigate();
  const loading = useAxiosLoader(false);

  const { userId, isOwner } = useUserContext();

  const defaultModel: AuthorizationInputDto = { userId };

  const form = useInputForm<AuthorizationInputDto>({
    defaultModel: defaultModel,
    addAsync: authorizationService.addAuthorizationAsync,
    updateAsync: authorizationService.updateAuthorizationAsync,
    validate: async (model: AuthorizationInputDto) => {
      if (model.issueDate && model.registrationNumber) {
        await authorizationService
          .getAuthorizationIdByYearAsync(userId, model.registrationNumber, model.issueDate)
          .then(({ data: authorizationId }) => {
            if (authorizationId && authorizationId !== form.id) {
              form.setError(nameof(defaultModel.registrationNumber), ErrorMessages.AlreadyExists);
            }
          });
      }
    },
  });

  useEffect(() => {
    (async () => {
      if (form.mode === Mode.EDIT) {
        await authorizationService.getAuthorizationAsync(form.id).then(({ data: authorization }) => {
          const model: AuthorizationInputDto = {
            ...authorization,
            userId: userId,
            authorizationId: form.id,
            authorizationRegisterIds: _.map(authorization.authorizationRegisters, (nom) => nom.id),
            fileId: authorization.file ? authorization.file.fileId : undefined,
          };

          if (isOwner(authorization.ownerUserIds)) {
            form.recreateForm(model);
          } else {
            navigate(PublicRoutesConfig.Home.Path);
          }
        });
      }
    })();
  }, []);

  const renderStepper = () => <LinearStepper step={form.step} mode={form.mode} />;

  const renderSuccess = () => (
    <Success routePath={PublicRoutesConfig.Authorization}>
      {`на пълномощно ${form.getValues(nameof(defaultModel.registrationNumber))} / ${dateToStringShort(
        form.getValues(nameof(defaultModel.issueDate))
      )}`}
    </Success>
  );

  const renderForm = () => (
    <form onSubmit={form.onSubmit}>
      <Stack spacing={3}>
        <SectionExtended title="Данни за пълномощно">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextFieldExtended
                label="Регистрационен номер"
                name={nameof(defaultModel.registrationNumber)}
                form={form}
                disabled={form.step === Step.PREVIEW || form.getValues(nameof(defaultModel.isWithdrawn))}
                rules={{
                  ...Rules.Required,
                  ...Rules.Number,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePickerExtended
                label="Дата"
                name={nameof(defaultModel.issueDate)}
                form={form}
                disabled={form.step === Step.PREVIEW || form.getValues(nameof(defaultModel.isWithdrawn))}
                rules={{
                  ...Rules.Required,
                  ...Rules.Date,
                }}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <SelectExtended
                label="Вид пълномощно"
                name={nameof(defaultModel.authorizationRegisterIds)}
                multiple
                form={form}
                disabled={form.step === Step.PREVIEW}
                searchRequest={registerService.getAuthorizationRegisters}
                initRequest={registerService.getAuthorizationRegistersByIds}
                rules={Rules.Required}
              />
            </Grid>
          </Grid>
        </SectionExtended>
        <SectionExtended title="Упълномощител/и">
          <TextFieldExtended
            name={nameof(defaultModel.authorizer)}
            multiline
            form={form}
            disabled={form.step === Step.PREVIEW}
            rules={Rules.Required}
          />
        </SectionExtended>
        <SectionExtended title="Упълномощен/и">
          <TextFieldExtended
            name={nameof(defaultModel.authorized)}
            multiline
            form={form}
            disabled={form.step === Step.PREVIEW}
            rules={Rules.Required}
          />
        </SectionExtended>
        <SectionExtended title="Описание">
          <TextFieldExtended
            name={nameof(defaultModel.description)}
            multiline
            form={form}
            disabled={form.step === Step.PREVIEW}
            rules={Rules.Required}
          />
        </SectionExtended>
        <SectionExtended
          title="Сканирано копие (незадължително)"
          defaultExpanded={!!form.watch(nameof(defaultModel.fileId))}
        >
          <DropzoneExtended
            name={nameof(defaultModel.fileId)}
            form={form}
            disabled={form.step === Step.PREVIEW}
            initRequest={fileService.getFileMetadataById}
            uploadRequest={fileService.upload}
          />
        </SectionExtended>
        {form.mode === Mode.EDIT && (
          <SectionExtended title="Забележка (причини за извършване на корекцията)">
            <TextFieldExtended
              name={nameof(defaultModel.correctionReason)}
              multiline
              form={form}
              disabled={form.step === Step.PREVIEW}
              rules={Rules.Required}
            />
          </SectionExtended>
        )}
        <Stack direction="row-reverse" sx={{ justifyContent: "space-between" }}>
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingPosition="start"
            size="large"
            startIcon={<CheckBoxOutlined />}
            onClick={() => form.scrollToFirstError()}
            type="submit"
          >
            {form.step === Step.INPUT ? "Преглед" : "Впиши"}
          </LoadingButton>

          {form.step === Step.PREVIEW && (
            <Button size="large" startIcon={<CreateOutlined />} onClick={() => form.onEdit()}>
              Редактирай
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );

  return (
    <Stack spacing={3} minHeight={550}>
      <Typography variant="h5">Регистър пълномощни</Typography>
      {renderStepper()}
      {form.step === Step.SUCCESS ? renderSuccess() : renderForm()}
    </Stack>
  );
}
